import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile
} from 'firebase/auth';
import { auth } from '../../../config/firebase';
import { PATHS } from '../../../config/paths';
import { useAuthStore } from '../store/authStore';

class AuthService {
  async loginWithEmail(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  async loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  }

  async registerWithEmail(email, password) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  }

  async updateProfile(displayName) {
    if (!auth.currentUser) {
      throw new Error('No authenticated user found');
    }
    await updateProfile(auth.currentUser, { displayName });
  }

  async getToken(forceRefresh = false) {
    const user = auth.currentUser;
    if (user) {
      return user.getIdToken(forceRefresh);
    }
    return null;
  }

  /**
   * Start token refresh interval
   * Refreshes the token every 50 minutes to ensure it never expires
   * Firebase tokens typically expire after 1 hour
   */
  startTokenRefresh() {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Set up new interval
    this.refreshInterval = setInterval(async () => {
      try {
        const token = await this.getToken(true); // Force token refresh
        if (token) {
          // Update token in auth store
          const authStore = useAuthStore.getState();
          authStore.setToken(token);
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If there's an error refreshing the token, stop the interval
        this.stopTokenRefresh();
      }
    }, 2 * 60 * 1000); // 2 minutes
  }

  /**
   * Stop token refresh interval
   */
  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Handle complete logout process including Firebase and local state
   * @returns {Promise<void>}
   */
  async handleLogout() {
    try {
      // Sign out from Firebase
      await signOut(auth);

      // Clear authentication state from Zustand store
      const authStore = useAuthStore.getState();
      await authStore.logout();

      // Clear any stored data or cookies
      localStorage.removeItem('user-settings');
      sessionStorage.clear();

      // Redirect to signin page using defined routes
      window.location.href = PATHS.AUTH.SIGNIN;
    } catch (error) {
      console.error('Error during logout:', error);
      // Ensure redirect happens even if there's an error
      window.location.href = PATHS.AUTH.SIGNIN;
    }
  }

  /**
   * Handle session expiration or invalid token
   * @returns {Promise<void>}
   */
  async handleSessionExpired() {
    await this.handleLogout();
    return Promise.reject(new Error('Session expired. Please login again.'));
  }

  // Reset password methods
  async sendPasswordResetEmail(email) {
    await sendPasswordResetEmail(auth, email);
  }

  async confirmPasswordReset(oobCode, newPassword) {
    await confirmPasswordReset(auth, oobCode, newPassword);
  }

  /**
   * Check if the current token is valid
   * @returns {boolean}
   */
  isValidToken() {
    const token = useAuthStore.getState().token;
    if (!token) return false;

    try {
      // Add any additional token validation logic here
      // For example, checking if it's expired based on JWT decode
      return true;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
